import { useQuery } from '@apollo/client';
import { Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useUNSAFE_PortalContext } from '@react-aria/overlays';
import { RiPencilLine, RiPriceTag3Line } from '@remixicon/react';
import { useEffect, useMemo, useState } from 'react';
import { AgButton, AgText } from 'src/components/ui';
import { useDebounce } from 'src/hooks/useDebounce';
import { DEBOUNCE_DELAY_LOW } from 'src/lib/constants';
import fuzzyComplete from 'src/utils/fuzzyComplete';
import { useDebounceValue } from 'usehooks-ts';
import AddLabelInput from './AddLabelInput';
import LabelsApi, { labelDefinitionPageInput } from './Labels.api';
import { useLabelAttachmentsStore } from './labelAttachments.store';
import { LabelDef, useLabelDefinitionsStore } from './labelDefinitions.store';
import useBulkLabelAttachment from './useBulkLabelAttachment';
import { labelDefinitionNodes } from './useLabelStoreHydration';

/** Typing pauses this long before the server search runs. */
export const LABEL_SEARCH_DEBOUNCE_MS = 300;

/**
 * The picker's server search (P7-R35, FU-LBLDEF). The store seed holds the first page by name; when the organization
 * has more, typing searches the server and merges the hits into the store, so every label can be found and picked.
 *
 *   open        ──► no request (the seed is already in the store)
 *   type "rus"  ──► one LabelDefinitionOptionsQuery after the debounce, only when the seed is not the whole list
 */
function useLabelDefinitionSearch(open: boolean, search: string) {
  const types = useLabelDefinitionsStore((state) => state.labelTypes);
  const moreLabelsOnServer = useLabelDefinitionsStore((state) => state.moreLabelsOnServer);
  const upsertAvailableLabels = useLabelDefinitionsStore((state) => state.upsertAvailableLabels);
  const [serverSearch] = useDebounceValue(search.trim(), LABEL_SEARCH_DEBOUNCE_MS);

  const { data } = useQuery(LabelsApi.SearchDefinitions, {
    variables: { types, input: labelDefinitionPageInput(serverSearch) },
    skip: !open || !moreLabelsOnServer || !serverSearch,
    // A rename or delete since the last identical search must not come back from the cache.
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data) upsertAvailableLabels(labelDefinitionNodes(data.labelDefinitionPage.edges));
  }, [data, upsertAvailableLabels]);

  return moreLabelsOnServer;
}

export default function AddLabelDropdown({ anchorEl }) {
  const open = useLabelAttachmentsStore((state) => state.dropdownOpen);
  const closeDropdown = useLabelAttachmentsStore((state) => state.closeDropdown);
  // When rendered inside an AgModal (react-aria), read the modal's portal container
  // so the MUI Menu renders inside the focus-trapped subtree. Without this, the
  // menu portals to document.body, which react-aria marks as `inert` — blocking
  // all pointer events.
  const { getContainer } = useUNSAFE_PortalContext();
  // getContainer() reads a ref (portalRef.current), so we only need to resolve it
  // when the portal context itself changes, not on every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally excluding getContainer() result
  const container = useMemo(() => getContainer?.(), [getContainer]);

  const [search, setSearch] = useState('');

  const labels = useLabelAttachmentsStore((state) => state.labels);
  const newLabelAttachments = useLabelAttachmentsStore((state) => state.newLabelAttachments);
  const labelDefinitions = useLabelDefinitionsStore((state) => [
    ...state.availableLabels,
    ...state.newLabelDefinitions,
  ]);

  const unattachedLabels = useMemo(() => {
    const allAttachedLabels = new Set(
      newLabelAttachments.map((attached) => attached.label).concat(labels.map((label) => label.labelDefinition.label))
    );

    return labelDefinitions.filter((label) => !allAttachedLabels.has(label.label));
  }, [newLabelAttachments, labelDefinitions, labels]);

  const handleSearchInputChange = useDebounce(setSearch, DEBOUNCE_DELAY_LOW);

  const fuzzySearch = useMemo(() => fuzzyComplete(['label']), []);
  const filteredLabels = useMemo(() => {
    return fuzzySearch(unattachedLabels, { inputValue: search });
  }, [search, fuzzySearch, unattachedLabels]);

  const moreLabelsOnServer = useLabelDefinitionSearch(open, search);

  const createAndAttachLabels = useBulkLabelAttachment();

  return (
    <Menu
      open={open}
      onClose={() => {
        createAndAttachLabels();
        closeDropdown();
      }}
      anchorEl={anchorEl?.current}
      container={container}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: -2, horizontal: 10 }}
      sx={{ zIndex: 210001, ['& .MuiList-root']: { pt: 0 } }}
    >
      <AddLabelInput handleSearchInputChange={handleSearchInputChange} firstOption={filteredLabels[0]} />
      <Divider />
      <div className="px-3 py-2">
        <AgText size="12" className="uppercase tracking-wider text-neutral-8" style={{ letterSpacing: '0.5px' }}>
          {moreLabelsOnServer
            ? 'Select a label, or type to search or create labels'
            : 'Select a label, or type to create new labels'}
        </AgText>
      </div>
      <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        {filteredLabels.map((labelDefinition) => (
          <LabelMenuItem key={labelDefinition.label + labelDefinition.id} labelDefinition={labelDefinition} />
        ))}
      </div>
    </Menu>
  );
}

function LabelMenuItem({ labelDefinition }: { labelDefinition: LabelDef }) {
  const addNewLabelAttachment = useLabelAttachmentsStore((state) => state.addNewLabelAttachment);
  const setEditingLabel = useLabelDefinitionsStore((state) => state.setEditingLabel);

  return (
    <MenuItem sx={{ gap: 1 }} onClick={() => addNewLabelAttachment(labelDefinition)}>
      <RiPriceTag3Line size={13} style={{ alignSelf: 'center', color: labelDefinition.color }} />
      <Typography color="textSecondary">{labelDefinition.label}</Typography>
      <AgButton
        variant="ghost"
        size="icon-sm"
        shape="circle"
        className="ml-auto"
        onPress={() => {
          setEditingLabel(labelDefinition);
        }}
      >
        <RiPencilLine size={16} />
      </AgButton>
    </MenuItem>
  );
}
